
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useClientRequests } from '@/hooks/admin/useClientRequests';
import { useEmployeeManagement } from '@/hooks/admin/useEmployeeManagement';
import { useNewsletterSubscribers } from '@/hooks/admin/useNewsletterSubscribers';
import { useContentGeneration } from '@/hooks/admin/useContentGeneration';
import { useDashboardInitialization } from '@/hooks/admin/useDashboardInitialization';

// Import all tab components
import AdminClientManagementTab from './AdminClientManagementTab';
import AdminVerifiedClientsTab from './AdminVerifiedClientsTab';
import AdminRejectedClientsTab from './AdminRejectedClientsTab';
import AdminEmployeesTab from './AdminEmployeesTab';
import AdminNewsletterTab from './AdminNewsletterTab';
import AdminContentMarketingTab from './AdminContentMarketingTab';
import AdminEmployeeLinksTab from './AdminEmployeeLinksTab';
import AdminSettingsTab from './AdminSettingsTab';
import AdminWaveIntegrationTab from './AdminWaveIntegrationTab';

const AdminDashboardContent: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>('clients');

  // Initialize active tab from navigation state
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  // Set up client requests management
  const clientRequests = useClientRequests();
  
  // Set up employee management
  const employeeManagement = useEmployeeManagement();

  // Set up newsletter management
  const newsletterManagement = useNewsletterSubscribers();

  // Set up content generation
  const contentGeneration = useContentGeneration();

  // Set up dashboard initialization
  const { handleRefresh } = useDashboardInitialization({
    loadOnboardingRequests: clientRequests.loadOnboardingRequests,
    loadEmployees: employeeManagement.loadEmployees,
    loadSubscribers: newsletterManagement.loadSubscribers
  });

  // Combined loading and processing states
  const isLoading = clientRequests.isLoading || newsletterManagement.isLoading;
  const isProcessing = clientRequests.isProcessing || employeeManagement.isProcessing || newsletterManagement.isProcessing;

  const handleTabRefresh = async () => {
    await handleRefresh(activeTab);
  };

  return (
    <div className="w-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-7 lg:grid-cols-7 w-full mb-8">
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="employees">Staff</TabsTrigger>
          <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="links">Links</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="wave">Wave</TabsTrigger>
        </TabsList>

        <TabsContent value="clients">
          <AdminClientManagementTab
            onboardingRequests={clientRequests.onboardingRequests}
            verifiedClients={clientRequests.verifiedClients}
            rejectedClients={clientRequests.rejectedClients}
            handleRequestAction={clientRequests.handleRequestAction}
            handleEditClient={clientRequests.handleEditClient}
            isProcessing={isProcessing}
            onRefresh={handleTabRefresh}
            handleDeleteClient={clientRequests.handleDeleteClient}
            handleToggleActive={clientRequests.handleToggleActive}
            handleSendInvitation={clientRequests.handleSendInvitation}
            handleSendBulkInvitations={clientRequests.handleSendBulkInvitations}
            onAddClient={() => clientRequests.setIsAddClientOpen(true)}
            filterStatus={clientRequests.filterStatus}
            onFilterChange={clientRequests.setFilterStatus}
          />
        </TabsContent>

        <TabsContent value="employees">
          <AdminEmployeesTab
            employees={employeeManagement.employees}
            isProcessing={isProcessing}
            onRefresh={handleTabRefresh}
            onAddEmployee={() => employeeManagement.setIsAddEmployeeOpen(true)}
            onRemoveEmployee={employeeManagement.handleRemoveEmployee}
            onToggleActive={employeeManagement.handleToggleEmployeeActive}
            filterStatus={employeeManagement.filterStatus}
            onFilterChange={employeeManagement.setFilterStatus}
          />
        </TabsContent>

        <TabsContent value="newsletter">
          <AdminNewsletterTab
            subscribers={newsletterManagement.subscribers}
            isLoading={isLoading}
            isProcessing={isProcessing}
            isSending={newsletterManagement.isSending}
            onRefresh={handleTabRefresh}
            onToggleStatus={newsletterManagement.handleToggleStatus}
            onDeleteSubscriber={newsletterManagement.handleDeleteSubscriber}
            onSendNewsletter={newsletterManagement.handleSendNewsletter}
          />
        </TabsContent>

        <TabsContent value="content">
          <AdminContentMarketingTab
            subscribers={newsletterManagement.subscribers}
            isLoading={isLoading}
            isProcessing={isProcessing}
            isSending={newsletterManagement.isSending}
            onToggleStatus={newsletterManagement.handleToggleStatus}
            onDeleteSubscriber={newsletterManagement.handleDeleteSubscriber}
            onSendNewsletter={newsletterManagement.handleSendNewsletter}
            onRefresh={handleTabRefresh}
            generatedContent={contentGeneration.generatedContent}
            blogTitle={contentGeneration.blogTitle}
            blogKeywords={contentGeneration.blogKeywords}
            onBlogGenerated={contentGeneration.handleBlogGenerated}
          />
        </TabsContent>

        <TabsContent value="links">
          <AdminEmployeeLinksTab />
        </TabsContent>

        <TabsContent value="settings">
          <AdminSettingsTab />
        </TabsContent>

        <TabsContent value="wave">
          <AdminWaveIntegrationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboardContent;
